import type { GAMProps } from "@sphtech/dmg-design-system";
import {
  AdsProvider,
  ExternalAdSlot,
  type PageTargetingProvider,
  PostitialClickGate,
  PrestitialGate,
} from "@sphtech/web2-ads";
import { useEffect, useMemo } from "react";

import type { StoryData } from "../../types/ServerCustomContext";
import { readLegacyGrapeshotTargeting } from "../trackers/grapeshotLegacy";
import {
  SDK_ADS_CONFIG_URL,
  SDK_ADS_ENV,
  SDK_ADS_PREBID_URL,
  SDK_ADS_RUNTIME_URL,
  SDK_ADS_SITE_KEY,
} from "./config";
import {
  buildLegacyGamBaseData,
  buildLegacyPageTargetingContext,
  publishLegacyGamBaseData,
  publishLegacyRecommendationSignals,
} from "./legacyGam";
import { buildPageAdsContext } from "./pageAds";
import SdkAdPlacements from "./SdkAdPlacements";

type Props = Omit<GAMProps, "StoryData"> & {
  StoryData?: Partial<StoryData>;
};

/**
 * SDK replacement for the legacy `AdSettings` engine bootstrap — a drop-in
 * sibling with the same props, mounted once per ads-enabled route. Owns the
 * `AdsProvider` (script bootstrap + page targeting) and the overlay formats;
 * in-page slots are portaled into the legacy placeholders by
 * `SdkAdPlacements`.
 */
export default function SdkAdsShell({
  pageType,
  currentLocation,
  StoryData,
  sectionName,
  pageTitle,
  adUnits,
}: Props) {
  const pathname = currentLocation.pathname;
  const context = useMemo(
    () => buildPageAdsContext(pageType, { pathname }, StoryData),
    [pageType, pathname, StoryData],
  );
  const isArticle = context.contentKind === "article";
  const legacyBaseData = useMemo(
    () =>
      buildLegacyGamBaseData({
        sectionName,
        pageType,
        pageTitle,
        adUnits,
        StoryData,
        currentLocation: { pathname },
      }),
    [adUnits, pageTitle, pageType, pathname, sectionName, StoryData],
  );
  const pageTargetingContext = useMemo(
    () => buildLegacyPageTargetingContext(context),
    [context],
  );
  const targetingProviders = useMemo<PageTargetingProvider[]>(
    () =>
      isArticle ? [() => readLegacyGrapeshotTargeting(StoryData?.path)] : [],
    [isArticle, StoryData?.path],
  );

  useEffect(() => {
    publishLegacyGamBaseData(legacyBaseData);
  }, [legacyBaseData]);

  useEffect(() => {
    publishLegacyRecommendationSignals();
  }, []);

  return (
    <AdsProvider
      siteKey={SDK_ADS_SITE_KEY}
      env={SDK_ADS_ENV}
      prebidUrl={SDK_ADS_PREBID_URL}
      configUrl={SDK_ADS_CONFIG_URL}
      runtimeUrl={SDK_ADS_RUNTIME_URL}
      pageTargetingContext={pageTargetingContext}
      targetingProviders={targetingProviders}
      targetingProviderKey={
        isArticle ? (StoryData?.path ?? pathname) : context.pageType
      }
      grapeshot={false}
    >
      <SdkAdPlacements context={context} />
      <ExternalAdSlot
        siteKey={SDK_ADS_SITE_KEY}
        placement="catfish"
        page={context.adSection}
      />
      <PrestitialGate page={context.adSection}>{null}</PrestitialGate>
      <PostitialClickGate page={context.adSection} />
    </AdsProvider>
  );
}
